import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Search, ExternalLink, Star, Shield, Info, Heart } from 'lucide-react';

const UTM_BASE = '?utm_source=hibff&utm_medium=marketplace&utm_campaign=';

const CATEGORIES = [
  { id: 'all', label: 'All Resources' },
  { id: 'books', label: 'Books & Courses' },
  { id: 'events', label: 'Events & Experiences' },
  { id: 'groups', label: 'Social Groups' },
  { id: 'coaching', label: 'Coaching' },
  { id: 'volunteering', label: 'Volunteering' },
  { id: 'fitness', label: 'Fitness & Social Sport' },
];

const RESOURCES = [
  // Tier 1 — HiBFF Curated
  { id: 1, title: 'How to Win Friends and Influence People', type: 'Book', tier: 1,
    category: 'books', author: 'Dale Carnegie', rating: 4.8,
    description: 'The timeless classic on building genuine human connections.',
    link: 'https://www.amazon.co.uk/dp/0091906814', hearts: 200 },
  { id: 2, title: 'Crucial Conversations', type: 'Book', tier: 1,
    category: 'books', author: 'Kerry Patterson', rating: 4.7,
    description: 'Tools for talking when stakes are high and emotions run strong.',
    link: 'https://www.amazon.co.uk/dp/1260474186', hearts: 200 },
  { id: 3, title: 'Local Meetup Finder', type: 'Tool', tier: 1,
    category: 'groups', author: 'Meetup.com', rating: 4.5,
    description: 'Find social groups and events happening near you this week.',
    link: 'https://www.meetup.com', hearts: null },
  { id: 4, title: 'Toastmasters International', type: 'Group', tier: 1,
    category: 'groups', author: 'Global Network', rating: 4.9,
    description: 'Practice public speaking and leadership in a supportive community.',
    link: 'https://www.toastmasters.org', hearts: null },
  { id: 5, title: '1-on-1 Social Skills Coaching', type: 'Service', tier: 1,
    category: 'coaching', author: 'HiBFF Coaches', rating: 5.0, hibff: true,
    description: '30-minute session with a certified social skills expert.',
    link: '#', hearts: 5000 },
  { id: 6, title: 'Community Events Calendar', type: 'Events', tier: 1,
    category: 'events', author: 'Your City', rating: 4.6,
    description: 'Weekly curated events for meeting new people in your area.',
    link: 'https://www.eventbrite.co.uk', hearts: null },
  { id: 7, title: 'The Art of Small Talk', type: 'Course', tier: 1,
    category: 'books', author: 'Debra Fine', rating: 4.4,
    description: 'Online course: Master the skill of starting conversations anywhere.',
    link: 'https://www.udemy.com', hearts: 800 },
  { id: 8, title: 'Volunteer Match', type: 'Tool', tier: 1,
    category: 'volunteering', author: 'VolunteerMatch.org', rating: 4.7,
    description: 'Find volunteering opportunities — the best way to meet kind people.',
    link: 'https://www.volunteermatch.org', hearts: null },

  // Tier 2 — Affiliate Partners (examples)
  { id: 9, title: 'Confident Conversations Masterclass', type: 'Course', tier: 2,
    category: 'books', author: 'Skillshare', rating: 4.6, affiliate: true,
    description: 'Build unshakeable confidence in any social situation. 14-day free trial.',
    link: 'https://www.skillshare.com', hearts: null, price: 'Free trial' },
  { id: 10, title: 'Speed Friending Events', type: 'Event', tier: 2,
    category: 'events', author: 'Eventbrite Partners', rating: 4.8, affiliate: true, featured: true,
    description: 'Meet 20+ new people in one evening. Events in major UK cities. HiBFF members get priority booking.',
    link: 'https://www.eventbrite.co.uk', hearts: null, price: 'From $15' },
  { id: 11, title: 'parkrun — Free Weekly Social Runs', type: 'Group', tier: 2,
    category: 'fitness', author: 'parkrun Global', rating: 4.9, affiliate: true,
    description: 'Free, weekly, community 5k events. The easiest way to meet active people.',
    link: 'https://www.parkrun.com', hearts: null },
  { id: 12, title: 'Do-It Volunteering', type: 'Tool', tier: 2,
    category: 'volunteering', author: 'Do-It.org', rating: 4.5, affiliate: true,
    description: 'UK volunteering platform — thousands of opportunities to connect through giving.',
    link: 'https://www.do-it.org', hearts: null },
  { id: 13, title: 'Communication Coach — Live Sessions', type: 'Service', tier: 2,
    category: 'coaching', author: 'BetterUp Partners', rating: 4.7, affiliate: true,
    description: 'Professional communication coaching. Build skills with a real human expert.',
    link: 'https://www.betterup.com', hearts: null, price: 'From $50/session' },
];

const Marketplace = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filtered = RESOURCES.filter(r => {
    const matchCat = selectedCategory === 'all' || r.category === selectedCategory;
    const matchSearch = !searchQuery || r.title.toLowerCase().includes(searchQuery.toLowerCase()) || r.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  }).sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  const buildLink = (r) => {
    if (!r.link || r.link === '#') return '#';
    const utm = r.affiliate ? `${UTM_BASE}affiliate_${r.id}` : `${UTM_BASE}curated_${r.id}`;
    return r.link.includes('?') ? `${r.link}&${utm.slice(1)}` : `${r.link}${utm}`;
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="marketplace-page">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Marketplace</h1>
        <p className="text-gray-600 mb-4">Books, courses, events, and groups to help you connect with real people.</p>

        {/* Tier 2 Affiliate Disclosure Banner */}
        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-2.5" data-testid="affiliate-disclosure">
          <Info size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-800">
            Some resources in our Marketplace are affiliate partnerships. HiBFF may earn a commission if you make a purchase. We only partner with organisations that help our users connect with real people.
          </p>
        </div>

        {/* Search + Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search resources..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-transparent"
              data-testid="marketplace-search"
            />
          </div>
          <div className="relative">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {CATEGORIES.map(c => (
                <button
                  key={c.id}
                  onClick={() => setSelectedCategory(c.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === c.id ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`marketplace-cat-${c.id}`}
              >
                {c.label}
                </button>
              ))}
            </div>
            <div className="sm:hidden pointer-events-none absolute inset-y-0 right-0 w-8" style={{ background: 'linear-gradient(to left, rgba(249,250,251,0.95), rgba(249,250,251,0))' }} aria-hidden="true" />
          </div>
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filtered.map(r => (
            <div key={r.id} className={`p-5 bg-white rounded-2xl border shadow-sm hover:shadow-md transition-shadow ${r.featured ? 'border-purple-300 ring-1 ring-purple-200' : 'border-gray-200'}`} data-testid={`resource-${r.id}`}>
              {r.featured && (
                <div className="mb-3 -mt-1 -mx-1">
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-gradient-to-r from-purple-600 to-orange-500 text-white">FEATURED PARTNER</span>
                </div>
              )}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{r.type}</span>
                  {r.hibff && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700" data-testid={`hibff-badge-${r.id}`}>
                      HiBFF
                    </span>
                  )}
                  {r.affiliate && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1" data-testid={`affiliate-badge-${r.id}`}>
                      <Shield size={10} /> Partner
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-amber-500">
                  <Star size={14} fill="currentColor" />
                  <span className="text-sm font-medium text-gray-700">{r.rating}</span>
                </div>
              </div>
              <h3 className="font-bold text-gray-900 mb-1">{r.title}</h3>
              <p className="text-xs text-gray-500 mb-2">by {r.author}</p>
              <p className="text-sm text-gray-600 mb-3">{r.description}</p>
              <div className="flex items-center justify-between">
                <div>
                  {r.hearts ? (
                    <span className="text-sm font-semibold text-purple-600 flex items-center gap-1">
                      <Heart size={14} fill="currentColor" /> {r.hearts.toLocaleString()} Hearts
                    </span>
                  ) : r.price ? (
                    <span className="text-sm font-semibold text-gray-700">{r.price}</span>
                  ) : (
                    <span className="text-sm font-semibold text-green-600">Free</span>
                  )}
                </div>
                <a
                  href={buildLink(r)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-100 transition-colors"
                  data-testid={`resource-link-${r.id}`}
                >
                  View <ExternalLink size={14} />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Become a Partner CTA */}
        <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="partner-cta">
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Does your organisation help people make real friends?</h3>
            <p className="text-sm text-gray-600">We partner with books, courses, events, coaches, and groups aligned with our mission.</p>
          </div>
          <a
            href="/partners"
            className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors whitespace-nowrap"
            data-testid="become-partner-btn"
          >
            Become a Partner
          </a>
        </div>

        {/* Mission Footer */}
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl text-center" data-testid="marketplace-mission">
          <p className="text-gray-900 font-medium text-sm">
            Every resource here helps you connect with real people. That&apos;s what HiBFF is for.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Marketplace;
